import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AddIcon from '@mui/icons-material/Add';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import PremiumSelect from '../components/PremiumSelect';
import ConfirmModal from '../components/ConfirmModal';
import AlertModal from '../components/AlertModal';
import PageContainer from '../components/ui/PageContainer';
import StatusChip, { StatusTone } from '../components/ui/StatusChip';
import { Feedback, FeedbackReceipt, ReverseFeedback, Cycle } from '../types';
import {
  isCycleClosedForSubmissions,
  mergeCyclesForFilter,
  buildCycleFilterSelectOptions,
  findCurrentActiveCycle,
} from '../utils/cycleFeedbackUi';

type FeedbackTabKey = 'received' | 'given';

const Feedbacks: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user, loading: authLoading } = authContext || {};
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [reverseFeedbacks, setReverseFeedbacks] = useState<ReverseFeedback[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<string>('all');
  const [cycleFilterReady, setCycleFilterReady] = useState(false);
  const prevActiveCycleIdRef = useRef<string | null>(null);
  const userPinnedCycleRef = useRef(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('received');
  const [deletingFeedbackId, setDeletingFeedbackId] = useState<string | null>(null);
  const [pendingDeleteFeedback, setPendingDeleteFeedback] = useState<Feedback | null>(null);
  const [deleteErrorAlert, setDeleteErrorAlert] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const loadCycles = useCallback(async (): Promise<void> => {
    try {
      const [allRes, activeRes] = await Promise.all([
        api.get<Cycle[]>('/cycles'),
        api.get<Cycle>('/cycles/active').catch(() => ({ data: undefined as Cycle | undefined })),
      ]);

      const activeFromApi = activeRes.data;
      const list = allRes.data.map((cycle) => {
        if (!activeFromApi) {
          return cycle;
        }
        return {
          ...cycle,
          active: cycle.id === activeFromApi.id,
        };
      });

      if (activeFromApi && !list.some((c) => c.id === activeFromApi.id)) {
        list.unshift({ ...activeFromApi, active: true });
      }

      setCycles(list);
    } catch (error) {
      console.error('Erro ao carregar ciclos:', error);
    }
  }, []);

  const loadFeedbacks = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);

      const feedbacksPromise = api.get<Feedback[]>('/feedbacks').catch((err: any) => {
        console.error('Erro ao carregar feedbacks:', err);
        return { data: [] };
      });

      const reversePromise = api.get<ReverseFeedback[]>('/feedbacks/reverse').catch((err: any) => {
        console.error('Erro ao carregar feedbacks reversos:', err);
        return { data: [] };
      });

      const [feedbacksRes, reverseRes] = await Promise.all([
        feedbacksPromise,
        reversePromise,
      ]);

      setFeedbacks((feedbacksRes.data as Feedback[]) || []);
      setReverseFeedbacks((reverseRes.data as ReverseFeedback[]) || []);
    } catch (error) {
      console.error('Erro ao carregar feedbacks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !authLoading) {
      loadFeedbacks();
      loadCycles();
    }
  }, [user, authLoading, loadFeedbacks, loadCycles]);

  /** Atualiza ciclos ao voltar à aba (ex.: após virada de ciclo no backend). */
  useEffect(() => {
    if (!user || authLoading) return;
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        void loadCycles();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, authLoading, loadCycles]);

  const markAsRead = async (id: string, isReverse: boolean = false): Promise<void> => {
    try {
      if (isReverse) {
        await api.patch(`/feedbacks/reverse/${id}/read`);
      } else {
        await api.patch(`/feedbacks/${id}/read`);
      }
      loadFeedbacks();
    } catch (error) {
      console.error('Erro ao marcar como lido:', error);
    }
  };

  const deleteFeedbackAsAdmin = useCallback(async (id: string): Promise<boolean> => {
    setDeletingFeedbackId(id);
    try {
      await api.delete(`/feedbacks/${id}`);
      await loadFeedbacks();
      return true;
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setDeleteErrorAlert({
        open: true,
        message: Array.isArray(msg) ? msg.join(', ') : msg || 'Não foi possível excluir o feedback.',
      });
      return false;
    } finally {
      setDeletingFeedbackId(null);
    }
  }, [loadFeedbacks]);

  const currentUserId = user?.id ?? '';
  const currentUserRole = user?.role;

  const allCyclesForFilter = useMemo(
    () => mergeCyclesForFilter(cycles, feedbacks, reverseFeedbacks),
    [cycles, feedbacks, reverseFeedbacks],
  );

  const cycleFilterOptions = useMemo(
    () => buildCycleFilterSelectOptions(allCyclesForFilter),
    [allCyclesForFilter],
  );

  const activeCycle = useMemo(
    () => findCurrentActiveCycle(allCyclesForFilter),
    [allCyclesForFilter],
  );

  /** Primeira carga: filtro aponta para o ciclo atual. */
  useEffect(() => {
    if (cycleFilterReady || !activeCycle?.id) return;
    setSelectedCycle(activeCycle.id);
    prevActiveCycleIdRef.current = activeCycle.id;
    setCycleFilterReady(true);
  }, [activeCycle?.id, cycleFilterReady]);

  /** Rotação automática: se o usuário acompanhava o ciclo anterior, segue o novo ciclo atual. */
  useEffect(() => {
    if (!activeCycle?.id) return;

    const prevActiveId = prevActiveCycleIdRef.current;
    if (
      prevActiveId &&
      prevActiveId !== activeCycle.id &&
      !userPinnedCycleRef.current &&
      (selectedCycle === prevActiveId || selectedCycle === activeCycle.id)
    ) {
      setSelectedCycle(activeCycle.id);
    }

    prevActiveCycleIdRef.current = activeCycle.id;
  }, [activeCycle?.id, selectedCycle]);

  useEffect(() => {
    if (selectedCycle === 'all') return;
    if (!allCyclesForFilter.some((c) => c.id === selectedCycle)) {
      if (activeCycle?.id) {
        setSelectedCycle(activeCycle.id);
        userPinnedCycleRef.current = false;
      } else {
        setSelectedCycle('all');
      }
    }
  }, [allCyclesForFilter, selectedCycle, activeCycle?.id]);

  const handleCycleFilterChange = useCallback(
    (value: string): void => {
      setSelectedCycle(value);
      userPinnedCycleRef.current = value !== 'all' && value !== activeCycle?.id;
    },
    [activeCycle?.id],
  );

  const filterFeedbacksByCycle = useCallback(
    <T extends { cycleId: string }>(feedbacksList: T[]): T[] => {
      if (selectedCycle === 'all') {
        return feedbacksList;
      }
      return feedbacksList.filter((f) => f.cycleId === selectedCycle);
    },
    [selectedCycle],
  );

  // Para o colaborador avaliado a API só devolve o recibo: nota e texto não vêm no payload.
  const receivedFeedbacks: FeedbackReceipt[] = filterFeedbacksByCycle(
    feedbacks.filter((f) => f.developerId === currentUserId)
  );
  const givenFeedbacks = filterFeedbacksByCycle(
    feedbacks.filter((f) => f.managerId === currentUserId)
  );
  const receivedReverseFeedbacks = filterFeedbacksByCycle(
    reverseFeedbacks.filter((f) => f.managerId === currentUserId)
  );
  const givenReverseFeedbacks = filterFeedbacksByCycle(
    reverseFeedbacks.filter((f) => f.developerId === currentUserId)
  );
  const unreadReceivedFeedbacks = receivedFeedbacks.filter((feedback) => !feedback.read).length;
  const unreadReceivedReverseFeedbacks = receivedReverseFeedbacks.filter((feedback) => !feedback.read).length;

  const currentCycleLabel = useMemo(() => {
    if (selectedCycle === 'all') {
      return 'Todos os ciclos';
    }

    return allCyclesForFilter.find((cycle) => cycle.id === selectedCycle)?.name || 'Ciclo selecionado';
  }, [allCyclesForFilter, selectedCycle]);

  const activeCycleForActions = useMemo(
    () => findCurrentActiveCycle(cycles) ?? activeCycle,
    [cycles, activeCycle],
  );
  const feedbackSubmissionsClosed = useMemo(
    () => (activeCycleForActions ? isCycleClosedForSubmissions(activeCycleForActions.deadlineDate) : false),
    [activeCycleForActions],
  );

  const openAdminDeleteDialog = useCallback(
    (feedback: Feedback): void => {
      if (!activeCycleForActions || feedback.cycleId !== activeCycleForActions.id) return;
      setPendingDeleteFeedback(feedback);
    },
    [activeCycleForActions],
  );

  const closeAdminDeleteDialog = useCallback((): void => {
    if (deletingFeedbackId) return;
    setPendingDeleteFeedback(null);
  }, [deletingFeedbackId]);

  const handleConfirmAdminDelete = useCallback(async (): Promise<void> => {
    if (!pendingDeleteFeedback) return;
    const ok = await deleteFeedbackAsAdmin(pendingDeleteFeedback.id);
    if (ok) setPendingDeleteFeedback(null);
  }, [pendingDeleteFeedback, deleteFeedbackAsAdmin]);

  const roleTitle = useMemo(() => {
    switch (currentUserRole) {
      case 'developer':
        return 'Seus feedbacks';
      case 'manager':
        return 'Painel de avaliações';
      case 'admin':
        return 'Visão geral de feedbacks';
      case 'hr':
        return 'Acompanhamento de feedbacks';
      default:
        return 'Feedbacks';
    }
  }, [currentUserRole]);

  const roleDescription = useMemo(() => {
    switch (currentUserRole) {
      case 'developer':
        return 'Acompanhe feedbacks recebidos, histórico por ciclo e devolutivas reversas enviadas.';
      case 'manager':
        return 'Gerencie feedbacks do time, acompanhe retornos recebidos e navegue pelos ciclos.';
      case 'admin':
      case 'hr':
        return 'Consulte avaliações da plataforma com filtros por ciclo e visão consolidada da operação.';
      default:
        return 'Acompanhe feedbacks por ciclo e perfil.';
    }
  }, [currentUserRole]);

  const summaryCards = useMemo(() => {
    if (currentUserRole === 'developer') {
      return [
        {
          label: 'Recebidos',
          value: receivedFeedbacks.length,
          description: 'Feedbacks técnicos disponíveis para leitura.',
        },
        {
          label: 'Não lidos',
          value: unreadReceivedFeedbacks,
          description: 'Avaliações que ainda pedem sua atenção.',
        },
        {
          label: 'Reversos enviados',
          value: givenReverseFeedbacks.length,
          description: 'Feedbacks reversos registrados por você.',
        },
      ];
    }

    if (currentUserRole === 'manager') {
      return [
        {
          label: 'Feedbacks criados',
          value: givenFeedbacks.length,
          description: 'Avaliações técnicas enviadas para o time.',
        },
        {
          label: 'Reversos recebidos',
          value: receivedReverseFeedbacks.length,
          description: 'Retornos recebidos da equipe.',
        },
        {
          label: 'Pendentes de leitura',
          value: unreadReceivedReverseFeedbacks,
          description: 'Feedbacks reversos aguardando leitura.',
        },
      ];
    }

    return [
      {
        label: 'Feedbacks filtrados',
        value: filterFeedbacksByCycle(feedbacks).length,
        description: 'Resultados no ciclo selecionado.',
      },
      {
        label: 'Ciclos disponíveis',
        value: allCyclesForFilter.length,
        description: 'Períodos disponíveis para filtrar (ativos e encerrados).',
      },
      {
        label: 'Filtro ativo',
        value: selectedCycle === 'all' ? 'Todos' : '1',
        description: currentCycleLabel,
      },
    ];
  }, [
    allCyclesForFilter.length,
    currentCycleLabel,
    feedbacks,
    filterFeedbacksByCycle,
    givenFeedbacks.length,
    givenReverseFeedbacks.length,
    receivedFeedbacks.length,
    receivedReverseFeedbacks.length,
    selectedCycle,
    unreadReceivedFeedbacks,
    unreadReceivedReverseFeedbacks,
    currentUserRole,
  ]);

  const feedbackTabsConfig = useMemo((): { key: FeedbackTabKey; label: string; count: number }[] => {
    if (currentUserRole === 'developer') {
      return [
        { key: 'received', label: 'Recebidos', count: receivedFeedbacks.length },
        { key: 'given', label: 'Reversos enviados', count: givenReverseFeedbacks.length },
      ];
    }
    if (currentUserRole === 'manager') {
      return [
        { key: 'given', label: 'Enviados', count: givenFeedbacks.length },
        { key: 'received', label: 'Reversos recebidos', count: receivedReverseFeedbacks.length },
      ];
    }
    return [];
  }, [
    currentUserRole,
    receivedFeedbacks.length,
    givenReverseFeedbacks.length,
    givenFeedbacks.length,
    receivedReverseFeedbacks.length,
  ]);

  const activeTabMeta = useMemo(() => {
    const hit = feedbackTabsConfig.find((t) => t.key === activeTab);
    return hit ?? feedbackTabsConfig[0];
  }, [feedbackTabsConfig, activeTab]);

  const formatDate = (value: string): string =>
    new Date(value).toLocaleDateString('pt-BR');

  const readStatusTone = (read: boolean): StatusTone => (read ? 'success' : 'warning');

  if (authLoading || loading || !user) {
    return (
      <PageContainer>
        <Stack spacing={2} sx={{ alignItems: 'center', py: 6 }}>
          <CircularProgress />
          <Typography variant="h6">Preparando feedbacks</Typography>
          <Typography variant="body2" color="text.secondary">
            Organizando ciclos, filtros e histórico da sua área.
          </Typography>
        </Stack>
      </PageContainer>
    );
  }

  const renderEmptyState = (message: string): React.ReactElement => (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="overline" color="text.secondary">
        Sem resultados
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
        {message}
      </Typography>
    </Paper>
  );

  const detailsLinkButton = (to: string): React.ReactElement => (
    <Button component={Link} to={to} variant="contained" size="small">
      Ver detalhes
    </Button>
  );

  const renderStandardFeedbackTable = (
    rows: Feedback[],
    config: {
      emptyMessage: string;
      personLabel: string;
      getPersonName: (feedback: Feedback) => string;
      getDetailsUrl: (feedback: Feedback) => string;
      showReadButton?: boolean;
      showAdminDelete?: (feedback: Feedback) => boolean;
    },
  ): React.ReactElement => {
    if (rows.length === 0) {
      return renderEmptyState(config.emptyMessage);
    }
    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider' }}
      >
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Ciclo</TableCell>
              <TableCell>{config.personLabel}</TableCell>
              <TableCell>Nota</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((feedback) => {
              const showAdminDelete = config.showAdminDelete?.(feedback) ?? false;
              const adminDeleteLoading = deletingFeedbackId === feedback.id;
              return (
                <TableRow key={feedback.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {feedback.cycle?.name || 'Ciclo não definido'}
                    </Typography>
                  </TableCell>
                  <TableCell>{config.getPersonName(feedback)}</TableCell>
                  <TableCell>{feedback.finalScore}</TableCell>
                  <TableCell>{formatDate(feedback.createdAt)}</TableCell>
                  <TableCell>
                    <StatusChip
                      label={feedback.read ? 'Lido' : 'Não lido'}
                      tone={readStatusTone(feedback.read)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}
                    >
                      {config.showReadButton && !feedback.read && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => markAsRead(feedback.id)}
                        >
                          Marcar como lido
                        </Button>
                      )}
                      {showAdminDelete && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          disabled={adminDeleteLoading}
                          onClick={() => openAdminDeleteDialog(feedback)}
                        >
                          {adminDeleteLoading ? 'A remover…' : 'Remover do ciclo'}
                        </Button>
                      )}
                      {detailsLinkButton(config.getDetailsUrl(feedback))}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderReverseFeedbackTable = (
    rows: ReverseFeedback[],
    config: {
      emptyMessage: string;
      personLabel: string;
      getPersonName: (feedback: ReverseFeedback) => string;
      getDetailsUrl: (feedback: ReverseFeedback) => string;
      showReadButton?: boolean;
    },
  ): React.ReactElement => {
    if (rows.length === 0) {
      return renderEmptyState(config.emptyMessage);
    }
    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider' }}
      >
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Ciclo</TableCell>
              <TableCell>{config.personLabel}</TableCell>
              <TableCell>Nota</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Leitura</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((feedback) => (
              <TableRow key={feedback.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {feedback.cycle?.name || 'Ciclo não definido'}
                  </Typography>
                </TableCell>
                <TableCell>{config.getPersonName(feedback)}</TableCell>
                <TableCell>{feedback.finalScore}</TableCell>
                <TableCell>{formatDate(feedback.createdAt)}</TableCell>
                <TableCell>
                  <StatusChip
                    label={feedback.read ? 'Lido' : 'Não lido'}
                    tone={readStatusTone(feedback.read)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}
                  >
                    {config.showReadButton && !feedback.read && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => markAsRead(feedback.id, true)}
                      >
                        Marcar como lido
                      </Button>
                    )}
                    {detailsLinkButton(config.getDetailsUrl(feedback))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderReceiptTable = (rows: FeedbackReceipt[]): React.ReactElement => {
    if (rows.length === 0) {
      return renderEmptyState('Nenhum feedback recebido ainda.');
    }
    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider' }}
      >
        <Table sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell>Ciclo</TableCell>
              <TableCell>Gestor</TableCell>
              <TableCell>Registado em</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((receipt) => (
              <TableRow key={receipt.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {receipt.cycle?.name || 'Ciclo não definido'}
                  </Typography>
                </TableCell>
                <TableCell>{receipt.manager?.name || 'Não informado'}</TableCell>
                <TableCell>{formatDate(receipt.createdAt)}</TableCell>
                <TableCell>
                  <StatusChip
                    label={receipt.read ? 'Recebimento confirmado' : 'A confirmar'}
                    tone={receipt.read ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell align="right">
                  {!receipt.read ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => markAsRead(receipt.id)}
                    >
                      Confirmar recebimento
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const showTabs =
    (user.role === 'developer' || user.role === 'manager') &&
    feedbackTabsConfig.length > 0 &&
    Boolean(activeTabMeta);

  return (
    <PageContainer
      icon={<RateReviewOutlinedIcon />}
      maxWidth="xl"
      title={roleTitle}
      subtitle={roleDescription}
      actions={
        <>
          {user.role === 'manager' && !feedbackSubmissionsClosed && (
            <Button component={Link} to="/feedbacks/create" variant="contained" startIcon={<AddIcon />}>
              Criar feedback
            </Button>
          )}
          {user.role === 'developer' && !feedbackSubmissionsClosed && (
            <Button component={Link} to="/feedbacks/reverse/create" variant="contained" startIcon={<AddIcon />}>
              Criar feedback reverso
            </Button>
          )}
          {(user.role === 'manager' || user.role === 'developer') && feedbackSubmissionsClosed && (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
              Envios encerrados: prazo de envio do ciclo já passou. Peça à administração para ampliar o
              prazo limite.
            </Typography>
          )}
        </>
      }
    >
      <Box sx={{ mb: { xs: 2, md: 3 }, maxWidth: { xs: '100%', sm: 360 } }}>
        <PremiumSelect
          label="Filtrar por ciclo"
          value={selectedCycle}
          options={[
            { value: 'all', label: 'Todos os ciclos' },
            ...cycleFilterOptions,
          ]}
          onChange={handleCycleFilterChange}
          placeholder="Selecione um ciclo..."
        />
      </Box>

      <Grid container spacing={2} sx={{ mb: { xs: 2, md: 3 } }}>
        {summaryCards.map((card) => (
          <Grid key={card.label} size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}
            >
              <Typography variant="overline" color="text.secondary">
                {card.label}
              </Typography>
              <Typography variant="h4" component="p" sx={{ fontWeight: 800, my: 0.5 }}>
                {card.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {showTabs && activeTabMeta && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, md: 3 } }}>
          <Tabs
            value={activeTabMeta.key}
            onChange={(_event, value: string) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {feedbackTabsConfig.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={`${t.label} (${t.count})`}
                icon={t.key === 'received' ? <MoveToInboxOutlinedIcon /> : <ReplyOutlinedIcon />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {activeTab === 'received' && user.role === 'developer' && renderReceiptTable(receivedFeedbacks)}

      {activeTab === 'given' &&
        user.role === 'manager' &&
        renderStandardFeedbackTable(givenFeedbacks, {
          emptyMessage: 'Nenhum feedback criado ainda.',
          personLabel: 'Desenvolvedor',
          getPersonName: (feedback) => feedback.developer?.name || 'Não informado',
          getDetailsUrl: (feedback) => `/feedbacks/${feedback.id}`,
        })}

      {activeTab === 'received' &&
        user.role === 'manager' &&
        renderReverseFeedbackTable(receivedReverseFeedbacks, {
          emptyMessage: 'Nenhum feedback reverso recebido ainda.',
          personLabel: 'Desenvolvedor',
          getPersonName: (feedback) =>
            feedback.anonymous ? 'Anônimo' : feedback.developer?.name || 'Não informado',
          getDetailsUrl: (feedback) => `/feedbacks/reverse/${feedback.id}`,
          showReadButton: true,
        })}

      {activeTab === 'given' &&
        user.role === 'developer' &&
        renderReverseFeedbackTable(givenReverseFeedbacks, {
          emptyMessage: 'Nenhum feedback reverso criado ainda.',
          personLabel: 'Gestor',
          getPersonName: (feedback) => feedback.manager?.name || 'Não informado',
          getDetailsUrl: (feedback) => `/feedbacks/reverse/${feedback.id}`,
        })}

      {(user.role === 'admin' || user.role === 'hr') &&
        renderStandardFeedbackTable(filterFeedbacksByCycle(feedbacks), {
          emptyMessage: 'Nenhum feedback encontrado para o filtro selecionado.',
          personLabel: 'Desenvolvedor',
          getPersonName: (feedback) => feedback.developer?.name || 'Não informado',
          getDetailsUrl: (feedback) => `/feedbacks/${feedback.id}`,
          showAdminDelete: (feedback) =>
            user.role === 'admin' &&
            Boolean(activeCycleForActions && feedback.cycleId === activeCycleForActions.id),
        })}

      <ConfirmModal
        isOpen={pendingDeleteFeedback !== null}
        title="Remover feedback do ciclo vigente"
        message={
          pendingDeleteFeedback
            ? `Remover o feedback técnico do ciclo vigente para ${
                pendingDeleteFeedback.developer?.name ?? 'o colaborador'
              }? O gestor poderá registar novamente a avaliação neste ciclo.`
            : ''
        }
        confirmLabel={deletingFeedbackId ? 'A remover…' : 'Remover'}
        cancelLabel="Cancelar"
        onConfirm={() => void handleConfirmAdminDelete()}
        onCancel={closeAdminDeleteDialog}
        confirmDisabled={deletingFeedbackId !== null}
      />

      <AlertModal
        isOpen={deleteErrorAlert.open}
        onClose={() => setDeleteErrorAlert({ open: false, message: '' })}
        title="Não foi possível remover"
        message={deleteErrorAlert.message}
        type="error"
      />
    </PageContainer>
  );
};

export default Feedbacks;
